import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentPlusIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const CURRENT_FILING_YEAR = new Date().getFullYear() - 1;
const PRIOR_FILING_YEAR = new Date().getFullYear() - 2;
const APPLICABLE_TAX_YEARS = [CURRENT_FILING_YEAR, PRIOR_FILING_YEAR];
const inputClass =
  'mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500';

function requestHeaders(json = false) {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function responseData(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = typeof data?.detail === 'string'
      ? data.detail
      : data?.detail
        ? JSON.stringify(data.detail)
        : `Request failed (${response.status})`;
    throw new Error(detail);
  }
  return data;
}

function hasApplicableTaxReturnDocument(item) {
  const uploads = (item?.uploads || []).filter((upload) => (
    upload.status === 'awaiting_review' || upload.status === 'accepted'
  ));
  if (!uploads.length) return false;

  const searchableText = [item?.name, ...uploads.map((upload) => upload.filename)]
    .filter(Boolean)
    .join(' ');
  return /tax\s*return/i.test(searchableText)
    && APPLICABLE_TAX_YEARS.some((year) => searchableText.includes(String(year)));
}

function affidavitPackets(checklist) {
  return (checklist || [])
    .flatMap((item) => (item?.trustee_docs || []).map((packet) => ({ item, packet })))
    .filter(({ item, packet }) => {
      const text = `${item?.name || ''} ${packet?.template?.name || ''}`;
      if (/\bdso\b|domestic support obligation/i.test(text)) return false;
      return packet?.template?.doc_type === 'tax_refund_affidavit'
        || /tax.*refund.*affidavit|affidavit.*tax.*refund/i.test(text);
    })
    .sort((left, right) => (
      new Date(right.packet.created_at || 0) - new Date(left.packet.created_at || 0)
    ));
}

function uploadedAffidavits(checklist) {
  return (checklist || [])
    .filter((item) => /tax.*refund.*affidavit|affidavit.*tax.*refund/i.test(item?.name || ''))
    .flatMap((item) => (item?.uploads || []).filter((upload) => (
      upload.status === 'awaiting_review' || upload.status === 'accepted'
    )))
    .sort((left, right) => new Date(right.uploaded_at || 0) - new Date(left.uploaded_at || 0));
}

function quickPayload(template, form) {
  const payload = {
    TAX_YEAR_1: form.topTaxYear,
    TAX_YEAR_1_REFUND_AMOUNT: form.topRefundAmount,
    TAX_REFUND_AMOUNT_1: form.topRefundAmount,
    TOP_TAX_YEAR: form.topTaxYear,
    TOP_REFUND_AMOUNT: form.topRefundAmount,
    TAX_YEAR_2: form.bottomTaxYear,
    TAX_YEAR_2_REFUND_AMOUNT: form.bottomRefundAmount,
    TAX_REFUND_AMOUNT_2: form.bottomRefundAmount,
    BOTTOM_TAX_YEAR: form.bottomTaxYear,
    BOTTOM_REFUND_AMOUNT: form.bottomRefundAmount,
  };

  (template?.field_schema || []).forEach((field) => {
    const key = String(field?.key || '').trim();
    if (!key) return;
    const description = `${key} ${field?.label || ''}`.toLowerCase();
    const isBottom = /bottom|second|(?:^|[_\s])2(?:$|[_\s])/.test(description);
    const isRefundAmount = /refund|amount|dollar/.test(description);
    const isTaxYear = /year/.test(description);
    if (isRefundAmount) {
      payload[key] = isBottom ? form.bottomRefundAmount : form.topRefundAmount;
    } else if (isTaxYear) {
      payload[key] = isBottom ? form.bottomTaxYear : form.topTaxYear;
    }
  });

  return payload;
}

function completedAffidavitForm(form) {
  const topComplete = Boolean(String(form.topTaxYear || '').trim())
    && form.topRefundAmount !== '';
  const bottomComplete = Boolean(String(form.bottomTaxYear || '').trim())
    && form.bottomRefundAmount !== '';
  return {
    topComplete,
    bottomComplete,
    values: {
      ...form,
      topTaxYear: topComplete ? form.topTaxYear : '',
      topRefundAmount: topComplete ? form.topRefundAmount : '',
      bottomTaxYear: bottomComplete ? form.bottomTaxYear : '',
      bottomRefundAmount: bottomComplete ? form.bottomRefundAmount : '',
    },
  };
}

export function taxRefundAffidavitIsApplicable(checklist) {
  return (checklist || []).some(hasApplicableTaxReturnDocument);
}

export default function TaxRefundAffidavitPanel({
  caseId,
  caseNumber = '',
  chapter,
  debtorEmails = [],
  initialChecklist = null,
  todoId = null,
  applicableOverride = false,
}) {
  const [checklist, setChecklist] = useState(
    Array.isArray(initialChecklist) ? initialChecklist : null,
  );
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(String(chapter) === '13');
  const [loadError, setLoadError] = useState('');
  // Keep the four quick-create fields visible as soon as the requirement appears.
  const [showCreate, setShowCreate] = useState(true);
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailComposer, setEmailComposer] = useState(null);
  const [loadingEmailContext, setLoadingEmailContext] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [form, setForm] = useState({
    topTaxYear: String(CURRENT_FILING_YEAR),
    topRefundAmount: '',
    bottomTaxYear: String(PRIOR_FILING_YEAR),
    bottomRefundAmount: '',
  });

  const template = useMemo(() => templates.find((item) => (
    item.doc_type === 'tax_refund_affidavit' && item.is_active !== false
  )), [templates]);
  const packets = useMemo(() => affidavitPackets(checklist), [checklist]);
  const affidavitUploads = useMemo(() => uploadedAffidavits(checklist), [checklist]);
  const currentPacket = packets.find(({ packet }) => (
    packet.generated_pdf || packet.generated_docx
  ));
  const hasAffidavitDocument = Boolean(currentPacket || affidavitUploads.length);
  const applicable = applicableOverride || taxRefundAffidavitIsApplicable(checklist);

  const refreshChecklist = async () => {
    const response = await fetch(`${API_URL}/case/${caseId}/checklist/`, {
      headers: requestHeaders(),
    });
    const data = await responseData(response);
    setChecklist(Array.isArray(data) ? data : []);
    return data;
  };

  useEffect(() => {
    let active = true;
    if (String(chapter) !== '13' || !caseId) {
      setLoading(false);
      return undefined;
    }

    async function load() {
      setLoading(true);
      setLoadError('');
      try {
        const [checklistResult, templatesResult] = await Promise.allSettled([
          fetch(`${API_URL}/case/${caseId}/checklist/`, { headers: requestHeaders() })
            .then(responseData),
          fetch(`${API_URL}/firm/trustee-doc-templates/`, { headers: requestHeaders() })
            .then(responseData),
        ]);
        if (!active) return;
        const errors = [];
        if (checklistResult.status === 'fulfilled') {
          const checklistData = checklistResult.value;
          setChecklist(Array.isArray(checklistData) ? checklistData : []);
        } else {
          errors.push(checklistResult.reason?.message || 'Could not load case documents.');
        }
        if (templatesResult.status === 'fulfilled') {
          const templateData = templatesResult.value;
          setTemplates(Array.isArray(templateData) ? templateData : templateData?.results || []);
        } else {
          errors.push(templatesResult.reason?.message || 'Could not load trustee templates.');
        }
        setLoadError(errors.join(' '));
      } catch (error) {
        if (active) setLoadError(error.message || 'Could not load the affidavit information.');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, [caseId, chapter]);

  if (String(chapter) !== '13') return null;
  if (loading && checklist === null && !applicableOverride) {
    return <div className="mt-4 text-sm text-slate-500">Checking for a Tax Refund Affidavit…</div>;
  }
  if (!applicable) return null;

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const createAffidavit = async (event) => {
    event.preventDefault();
    setActionError('');
    setSuccess('');
    if (!template) {
      setActionError('No active Tax Refund Affidavit template is available.');
      return;
    }
    if (form.topRefundAmount !== '' && !String(form.topTaxYear || '').trim()) {
      setActionError('Enter the tax year for the top refund amount.');
      return;
    }
    if (form.bottomRefundAmount !== '' && !String(form.bottomTaxYear || '').trim()) {
      setActionError('Enter the tax year for the bottom refund amount.');
      return;
    }
    const completedForm = completedAffidavitForm(form);
    if (!completedForm.topComplete && !completedForm.bottomComplete) {
      setActionError('Complete at least one tax-year section. Leave the other refund amount blank if that section is not needed.');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(
        `${API_URL}/firm/trustee-doc-templates/${template.id}/generate/`,
        {
          method: 'POST',
          headers: requestHeaders(true),
          body: JSON.stringify({
            case_id: caseId,
            payload: quickPayload(template, completedForm.values),
            checklist_name: template.default_checklist_name || 'Tax Refund Affidavit',
            checklist_description: template.default_checklist_description
              || 'Please complete the expense accounting, sign the affidavit, and upload the completed document.',
            is_required: true,
          }),
        },
      );
      await responseData(response);
      await refreshChecklist();
      setShowCreate(false);
      setSuccess('Tax Refund Affidavit created and added to the client document checklist.');
    } catch (error) {
      setActionError(error.message || 'Could not create the affidavit.');
    } finally {
      setCreating(false);
    }
  };

  const openGeneratedDocument = async (packet, kind) => {
    setActionError('');
    try {
      const response = await fetch(
        `${API_URL}/firm/trustee-doc-packets/${packet.id}/presign/?kind=${kind}`,
        { headers: requestHeaders() },
      );
      const data = await responseData(response);
      if (!data.url) throw new Error('No document link was returned.');
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setActionError(error.message || 'Could not open the affidavit.');
    }
  };

  const openEmailComposer = async (packet) => {
    setActionError('');
    setSuccess('');
    setLoadingEmailContext(true);
    try {
      const todoQuery = todoId ? `?todo_id=${encodeURIComponent(todoId)}` : '';
      const response = await fetch(
        `${API_URL}/case/${caseId}/trustee-doc-packets/${packet.id}/email/${todoQuery}`,
        { headers: requestHeaders() },
      );
      const context = await responseData(response);
      if (!context.assigned_employee) {
        throw new Error('Assign this trustee-document todo to an employee before composing the email.');
      }
      const employeeFirstName = context.assigned_employee.first_name;
      const recipients = (context.debtor_emails || debtorEmails || []).filter(Boolean).join(', ');
      const attorneyCc = (context.attorney_cc || []).filter(Boolean).join(', ');
      setEmailComposer({
        packet,
        assignedEmployee: context.assigned_employee,
        to: recipients,
        cc: attorneyCc,
        subject: context.subject || `Tax Refund Affidavit — Bankruptcy Case ${caseNumber || caseId}`,
        body: context.body || `Hello!\n\nThis is ${employeeFirstName} at Cisowski Law.\n\nAs a requirement for your Ch 13 case, the trustee requires anybody who received a tax refund over $1000 to detail how it was used.\n\nPlease complete the attached Tax Refund Affidavit. For each tax year, list how the refund was used and the cost of each expense.\n\nDo your best using bank statements to refresh your memory if needed. Try to get the sum of the expenses to equal the total of the tax refund amount. Please sign the completed affidavit and return it to us. If this is a joint case, both debtors should sign.\n\nPlease let us know if you have any questions.\n\nSincerely,\n${employeeFirstName}\nCisowski Law`,
      });
    } catch (error) {
      setActionError(error.message || 'Could not prepare the affidavit email.');
    } finally {
      setLoadingEmailContext(false);
    }
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    if (!emailComposer) return;
    setSendingEmail(true);
    setActionError('');
    try {
      const response = await fetch(
        `${API_URL}/case/${caseId}/trustee-doc-packets/${emailComposer.packet.id}/email/`,
        {
          method: 'POST',
          headers: requestHeaders(true),
          body: JSON.stringify({
            to: emailComposer.to,
            cc: emailComposer.cc,
            subject: emailComposer.subject,
            body: emailComposer.body,
            todo_id: todoId,
          }),
        },
      );
      const data = await responseData(response);
      setEmailComposer(null);
      setSuccess(`Affidavit emailed to ${(data.to || []).join(', ')}.`);
      await refreshChecklist();
    } catch (error) {
      setActionError(error.message || 'Could not send the affidavit email.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <section className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 text-slate-700">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h4 className="font-semibold text-slate-900">Send Tax Refund Affidavit</h4>
          <p className="mt-1 text-sm">
            A {APPLICABLE_TAX_YEARS.join(' or ')} tax return is on file for this Chapter 13 case.
          </p>
          {currentPacket && (
            <p className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-green-700">
              <CheckCircleIcon className="h-4 w-4" />
              Affidavit created{currentPacket.packet.status === 'sent_to_client' ? ' and emailed to the client' : ''}.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {currentPacket?.packet.generated_pdf && (
            <button
              type="button"
              onClick={() => openGeneratedDocument(currentPacket.packet, 'pdf')}
              className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" /> Open PDF
            </button>
          )}
          {currentPacket?.packet.generated_docx && (
            <button
              type="button"
              onClick={() => openGeneratedDocument(currentPacket.packet, 'docx')}
              className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" /> Open DOCX
            </button>
          )}
          {affidavitUploads.map((upload, index) => upload.signed_url && (
            <a
              key={upload.id}
              href={upload.signed_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-white px-3 py-2 text-sm font-semibold text-green-700 hover:bg-green-50"
              title={upload.filename}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Open uploaded affidavit{affidavitUploads.length > 1 ? ` ${index + 1}` : ''}
            </a>
          ))}
          {currentPacket && (
            <button
              type="button"
              onClick={() => openEmailComposer(currentPacket.packet)}
              disabled={loadingEmailContext}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {loadingEmailContext
                ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Preparing…</>
                : <><EnvelopeIcon className="h-4 w-4" /> Compose email</>}
            </button>
          )}
          {!hasAffidavitDocument && !showCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              <DocumentPlusIcon className="h-4 w-4" /> Create affidavit
            </button>
          )}
        </div>
      </div>

      {loadError && <p className="mt-3 text-sm font-medium text-red-700">{loadError}</p>}
      {actionError && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <ExclamationTriangleIcon className="h-5 w-5 flex-none" /> {actionError}
        </div>
      )}
      {success && <p className="mt-3 text-sm font-medium text-green-700">{success}</p>}

      {!hasAffidavitDocument && !template && !loading && (
        <p className="mt-3 text-sm text-amber-800">
          Add or activate a Tax Refund Affidavit template under Trustee Doc Templates before creating this document.
        </p>
      )}

      {showCreate && !hasAffidavitDocument && (
        <form onSubmit={createAffidavit} className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-slate-700">
              Top tax year
              <input className={inputClass} type="number" value={form.topTaxYear} onChange={(event) => updateForm('topTaxYear', event.target.value)} />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Top refund amount
              <input className={inputClass} type="number" min="0" step="0.01" value={form.topRefundAmount} onChange={(event) => updateForm('topRefundAmount', event.target.value)} />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Bottom tax year
              <input className={inputClass} type="number" value={form.bottomTaxYear} onChange={(event) => updateForm('bottomTaxYear', event.target.value)} />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Bottom refund amount
              <input className={inputClass} type="number" min="0" step="0.01" value={form.bottomRefundAmount} onChange={(event) => updateForm('bottomRefundAmount', event.target.value)} />
            </label>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Complete either section or both. Leave a section's refund amount blank to omit that entire section from the affidavit.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" disabled={creating} onClick={() => setShowCreate(false)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={creating || !template} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
              {creating ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Creating…</> : <><DocumentPlusIcon className="h-4 w-4" /> Create affidavit</>}
            </button>
          </div>
        </form>
      )}

      {emailComposer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="tax-affidavit-email-title">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 id="tax-affidavit-email-title" className="text-lg font-semibold text-slate-900">Email Tax Refund Affidavit</h2>
                <p className="mt-1 text-sm text-slate-500">
                  From {emailComposer.assignedEmployee.first_name} at Cisowski Law. Review the message before sending.
                </p>
              </div>
              <button type="button" disabled={sendingEmail} onClick={() => setEmailComposer(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Close email composer">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={sendEmail} className="space-y-4 px-5 py-5">
              <label className="block text-sm font-medium text-slate-700">
                To
                <input className={inputClass} value={emailComposer.to} onChange={(event) => setEmailComposer((current) => ({ ...current, to: event.target.value }))} placeholder="debtor@example.com" required autoFocus />
                <span className="mt-1 block text-xs font-normal text-slate-500">Separate multiple addresses with commas or semicolons.</span>
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Attorney CC (automatically included)
                <input className={`${inputClass} bg-slate-100`} value={emailComposer.cc} readOnly />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Subject
                <input className={inputClass} maxLength="255" value={emailComposer.subject} onChange={(event) => setEmailComposer((current) => ({ ...current, subject: event.target.value }))} required />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Message
                <textarea className={inputClass} rows="9" maxLength="50000" value={emailComposer.body} onChange={(event) => setEmailComposer((current) => ({ ...current, body: event.target.value }))} required />
              </label>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <strong>Attachment:</strong>{' '}
                {emailComposer.packet.generated_pdf ? 'Tax Refund Affidavit PDF' : 'Tax Refund Affidavit DOCX'}
              </div>
              <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                <button type="button" disabled={sendingEmail} onClick={() => setEmailComposer(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={sendingEmail} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                  {sendingEmail ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Sending…</> : <><PaperAirplaneIcon className="h-4 w-4" /> Send email</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
